import { Component, EventEmitter, Input, Output, OnInit, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { SearchService } from '@core/services/search.service';
import { ContactSearch } from '@src/app/models/contactSearch';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap, catchError, map, finalize } from 'rxjs/operators';
import { FormBuilder, FormGroup } from '@angular/forms';

@Component({
  selector: 'tlp-contact-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
})
export class SearchComponent implements OnInit, AfterViewInit, OnChanges {
  suggestions$: Observable<ContactSearch[]>;
  allContacts: ContactSearch[] = []; // Store all contacts for preselection
  errorMessage$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false); // Loading suggestions
  isPreselecting$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false); // Loading preselected
  hasClicked$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  @Output() contactSelected = new EventEmitter<ContactSearch>(); // Emits single contact when multiple=false
  @Output() selectedItems = new EventEmitter<ContactSearch[]>(); // Emits array always
  @Input() multiple: boolean = false;
  @Input() maxSelectedItems: number = 1;
  @Input() preselectedContactIds: number[] = []; // Input for preselected IDs
  @Input() tenantsOnly: boolean = false

  searchInput$ = new Subject<string>();
  searchForm: FormGroup;
  selectedContacts: ContactSearch[] = []; // Internal list for ngModel binding

  constructor(private searchService: SearchService, private fb: FormBuilder) {
    this.searchForm = this.fb.group({
      search: ['']
    });
    this.suggestions$ = this.initializeTypeahead();
  }

  ngOnInit(): void {
    // Initial load of preselected contacts if IDs are provided on init
    this.suggestions$.subscribe({
      next: () => {
        if(this.preselectedContactIds)
        this.loadPreselectedContacts(this.preselectedContactIds);
        this.errorMessage$.next(null); // Clear error message on successful load
      },
      error: (error) => {
        console.error('Error loading suggestions:', error);
        this.errorMessage$.next('Failed to load suggestions. Please try again.');
      }
    });
    
  }

  ngAfterViewInit(): void {
    // console.log('Component view initialized');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['preselectedContactIds'] && changes['preselectedContactIds'].currentValue) {
      // Load preselected contacts if the input array changes after initialization
      const currentIds = changes['preselectedContactIds'].currentValue;
      this.loadPreselectedContacts(currentIds);
    }
  }

  private loadPreselectedContacts(ids: number[]): void {
    if (true || ids && ids.length > 0 || this.allContacts.length == 0) {

      this.getContactPromise('').pipe(
        map(contacts => contacts.map(contact => ({
          ...contact,
          displayName: `${contact.firstName?.trim()} ${contact.lastName?.trim()} (${contact.email?.trim()})` // Trim whitespace
        })))
      ).subscribe({
        next: (contactsWithDisplayName) => {
          this.allContacts = contactsWithDisplayName; // Store all contacts with displayName
          const preselected = contactsWithDisplayName.filter(contact => ids.includes(contact.id));
          // Ensure object references match if ng-select compares by reference
          this.selectedContacts = preselected; 
          this.isPreselecting$.next(false); // Set to false after loading
        },
        error: (error) => {
          console.error('Error loading preselected contacts:', error);
          this.errorMessage$.next('Failed to load preselected contacts. Please try again.');
        }
      });
    }
    
  }


  initializeTypeahead(): Observable<ContactSearch[]> {

    return this.searchInput$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => this.isLoading$.next(true)),
      switchMap(term => this.getContactPromise(term).pipe(
        tap(() => this.isLoading$.next(false)),
        catchError(error => {
          console.error('Error loading contacts:', error);
          this.errorMessage$.next('Failed to load contacts. Please try again.');
          this.isLoading$.next(false);
          return [];
        })
      )),
      map(data => data.map(contact => ({
        ...contact,
        displayName: `${contact.firstName?.trim()} ${contact.lastName?.trim()} (${contact.email?.trim()})` // Trim whitespace
      })))
    );
  }

  getContactPromise(term: string) : Observable<ContactSearch[]>{
    
    return (this.tenantsOnly) ?
    this.searchService.searchForPossibleTenants(term) :
    this.searchService.searchAddressBook(term);
  }

  // Called when the selection changes in ng-select (due to user interaction or ngModel update)
  onSelectionChange(selected: ContactSearch | ContactSearch[] | null): void {
    // ng-select might return null if cleared, handle this case
    if (selected === null) {
      this.selectedContacts = [];
    } else if (Array.isArray(selected)) {
      this.selectedContacts = selected;
    } else {
      // Single selection mode
      this.selectedContacts = [selected];
    }
    this.emitSelection();
  }
  
  // Called when the selection dropdown opens
  // if the maxSelectedItems = 1 then it clears the current selection
  onSelectionOpen(selected: ContactSearch | ContactSearch[] | null): void {
    // ng-select might return null if cleared, handle this case
    if (this.selectedContacts.length > 0 && this.maxSelectedItems==1) {
      this.selectedContacts = [];
    } 
  }

  // Helper method to emit the selection events
  private emitSelection(): void {
    // Always emit the full array
    this.selectedItems.emit(this.selectedContacts);

    // Emit the single contact only if not in multiple mode and exactly one item is selected
    if ( this.selectedContacts.length === 1) {
      this.contactSelected.emit(this.selectedContacts[0]);
    } else if (!this.multiple && this.selectedContacts.length === 0) {
      // Emit null or undefined if selection is cleared in single mode
      this.contactSelected.emit(undefined); 
    }
    // In multiple mode, contactSelected is not emitted based on this logic
  }

  onNgSelectClick(): void {
    this.hasClicked$.next(true);
    // Trigger the typeahead logic by emitting an empty string
    this.searchInput$.next('');
  }

  
  /**
   * Public method to refresh contacts from API
   * Can be called from parent component via ViewChild
   */
  public refreshContactsList(ids: number[] = []): void {
    this.preselectedContactIds = [...this.preselectedContactIds, ...ids];
    this.loadPreselectedContacts(this.preselectedContactIds);
  }
}
